/**
 * V-A-3 — Single source of truth for the scheduling-mutation allowlist.
 *
 * Imported from BOTH:
 *  - src/utils/mutateTicketSchedule.ts            (V-A-1, client)
 *  - supabase/functions/_shared/mutateTicketSchedule.ts (V-A-2, server)
 *
 * This file is intentionally zero-dependency (no other imports, no
 * Deno-specific or browser-specific syntax) so it can be bundled by Vite
 * AND interpreted directly by Deno without translation. Keep it that way —
 * if you need to reference another module from here, instead inline the
 * value or move the consumer's logic out.
 *
 * Adding a field expands the scheduling truth surface: every helper that
 * gates writes by this allowlist will start accepting it. Treat additions
 * as a V-A-* change and review accordingly.
 */
export const ALLOWED_SCHEDULE_FIELDS = [
  'scheduled_date',
  'start_time',
  'opti_finish',
  'assigned_tech_id',
  'predicted_minutes',
  'bay_id',
  'parking_spot_id',
  'scheduled_start_time',
  'timer_status',
  'actually_started',
  'actual_start_time',
  // V-A-26: 'status' is genuinely scheduling-state — the canonical enum
  // {Active, Paused, Completed, Blocked, Deleted, Cancelled} controls
  // whether a ticket is schedulable (Active/Paused), terminal (Completed),
  // or tombstoned (Cancelled/Deleted/Blocked). Adding it to the allowlist
  // unlocks status as a valid CAS predicate target (e.g. "only update if
  // status is still Active or Paused" — `syncConvertedRolloverGrowthOnBoundary`
  // pattern). Direct `status` writes via the schedule-update path remain
  // unusual; most status changes flow through `mutateTicketTimerState`
  // transitions which encode their own valid status enum values.
  'status',
] as const;

export type ScheduleField = typeof ALLOWED_SCHEDULE_FIELDS[number];

export type ScheduleUpdates = Partial<Record<ScheduleField, unknown>>;
